import { ErrorHandler, Injectable } from '@angular/core';
import { Observable, BehaviorSubject, pipe, forkJoin, of, combineLatest, throwError, zip } from 'rxjs';
import { map, finalize, first, takeWhile, catchError, withLatestFrom, takeUntil, mergeMap, sample, tap, filter } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { LgvModel } from '../../data-models/longevity.model';
//import { LgvDomainService } from '../services/lgv-domain.service';
import { environment as env } from '../../../environments/environment';
import { LgvAuthMgrService } from './lgv-auth-mgr.service';
import { LgvUserService } from './lgv-user.service';
import { NotificationService } from '../notifications/notification.service';
import { LgvPublicRecordModel } from '../../data-models/lgv-publicRecord.model';
import { LgvClientService } from './lgv-client.service';
import { Router } from '@angular/router';
import { BusinessType } from '../enums/shared';
import { LgvPropertyService } from './lgv-property.service';
import { LgvMockDataService } from './lgv-mock-data.service';

@Injectable({
    providedIn: 'root'
})
export class LgvAPIService {

    _Auth0RegToken: LgvModel.UserToken = null;
    Auth0RegToken$ = new BehaviorSubject(this._Auth0RegToken);

    _PendingAuth0Registration: LgvModel.TokenLookupInfo = null;
    PendingAuth0Registration$ = new BehaviorSubject(this._PendingAuth0Registration);
    get HaveAuth0RegistrationInfo$(): Observable<boolean> { return this.PendingAuth0Registration$.pipe(map(x => x && x.email != null)) }

    _Auth0RegistrationState: string = '';
    Auth0RegistrationState$ = new BehaviorSubject(this._Auth0RegistrationState);

    SampleClientDataHasChanged$ = new BehaviorSubject(false); // call / update after finishished loading SampleClients$
    SampleClients$ = new BehaviorSubject([]);
    HasRealClient$ = new BehaviorSubject(null);


    get ShowOnboardingBanner$(): Observable<boolean> {

        return this.lgvUserSvc.isOnboarding$.pipe(
            withLatestFrom(
                combineLatest([this.HasRealClient$, this.lgvUserSvc.isREAgent$])
            ),
            //tap(([isOnboarding, [hasRealClients, isREAgent]]) => { console.log(`t1: ${isOnboarding} | ${hasRealClients} \ ${isREAgent}`) }),
            filter(([isOnboarding, [hasRealClients, isREAgent]]) => isOnboarding !== null && hasRealClients !== null && isREAgent !== null),
            //tap(([isOnboarding, [hasRealClients, isREAgent]]) => { console.log(`t2: ${isOnboarding} | ${hasRealClients} \ ${isREAgent}`) }),
            map(([isOnboarding, [hasRealClients, isREAgent]]) => {
                return isOnboarding || (!hasRealClients && isREAgent);
            })
        )

    }






    get IsClientRegistering$(): Observable<boolean> { return this.Auth0RegistrationState$.pipe(map(x => x == 'Registering')) }
    get HaveSuccessfulRegistration$(): Observable<boolean> { return this.Auth0RegistrationState$.pipe(map(x => x == 'Completed')) }
    get HaveRegistrationError$(): Observable<boolean> { return this.Auth0RegistrationState$.pipe(map(x => x == 'Error')) }


    _Theme: string = null;
    Theme$ = new BehaviorSubject(this._Theme);



    // Agent$, AgentSS$, and DBUserInfo$ have been moved to LgvUserService.
    // Keeping these around because so many pages refer to this locaiton.
    // Eventually, the pages should user LgvUserSvc instead of LgvAPiSvc
    get Agent$() { return this.lgvUserSvc.Agent$; }
    get AgentSS$() { return this.lgvUserSvc.AgentSS$; }
    get DBUserInfo$() { return this.lgvUserSvc.DBUserInfo$; }


    //_Company: LgvModel.CompanyConfig = null;
    //Company$ = new BehaviorSubject(this._Company);


    _Partner: LgvModel.Agent = null;
    Partner$ = new BehaviorSubject(this._Partner);

    _PartnerCompany: LgvModel.CompanyConfig = null;
    PartnerCompany$ = new BehaviorSubject(this._PartnerCompany);

    _CompanyInfo: LgvModel.CompanyInfo[] = [];

    //kxk: Do we need list of companies here, or can we use the list from lgvCompanySvc?  At minimum, we can share...
    Companies$ = new BehaviorSubject(this._CompanyInfo);
    businessTypes$ = new BehaviorSubject([]);
    socialMediaTypes$ = new BehaviorSubject([]);

    get IsClient(): boolean { return this.lgvAuthMgr.IsClient; }
    get IsAgent(): boolean { return this.lgvUserSvc.IsAgent; }
    get IsAdmin(): boolean { return this.lgvUserSvc.IsAdmin; }
    get defaultHomePage$(): Observable<string> { return this.IsClient ? this.lgvClientSvc.defaultClientHomePage$ : this.lgvUserSvc.defaultUserHomePage$ }
    get lenderPartners$(): Observable<LgvModel.CompanyInfo[]> { return this.Companies$.pipe(map(array => array.filter(f => f.businessTypeId?.toUpperCase() === BusinessType.MortgageLender))) }


    constructor(private http: HttpClient,
        private lgvUserSvc: LgvUserService,
        private lgvClientSvc: LgvClientService,
        private lgvPropertySvc: LgvPropertyService,
        private lgvMockDataSvc: LgvMockDataService,
        private notificationSvc: NotificationService,
        private lgvAuthMgr: LgvAuthMgrService,
        private router: Router,
        private NotificationSvc: NotificationService
    ) {

        this.lgvAuthMgr.IsAuthenticated$.subscribe(x => {

            // Only load companies and business types for non-clients
            if (!x || this.lgvAuthMgr.IsClient) {
                return;
            }

            this.loadCompanies();
            this.loadBusinessTypes();
            this.loadSocialMediaTypes();
        });

        // adding the test for sample clients here because we need access to both the client service and user service.
        this.lgvUserSvc.isOnboarding$.pipe(filter(f=> !!f)).subscribe(isOnboarding => {
            if (this.lgvUserSvc.IsAgent) {
                this.checkForSampleClientsAndAddIfNeeded(isOnboarding);
            }
        });

    }

    public loadCompanies() {
        this.fetchCompanies('', '&$top=200&$orderby=name').pipe(
            takeWhile(x => x !== null))
            .subscribe(res => {
                this.Companies$.next(res.value);
            },
                (err: Error) => {
                    const msg = 'There was an error loading the list of companies';
                    console.log(msg);
                    this.NotificationSvc.error(msg);
                }
            );
    }

    public loadBusinessTypes() {
        this.fetchBusinessTypes().pipe(takeWhile(x => x !== null)).subscribe(res => {
            this.businessTypes$.next(res);
        },
            (err: Error) => {

                // check for 401s from /business-types.  It's usually the first call to fail if user is not properly set up
                if (err instanceof HttpErrorResponse && err.status === 401) {
                    let errorMsg = `Sorry, we can't find your RealFinity account.`;
                    this.router.navigate([`/default/error/${errorMsg}`]);
                }
                else {
                    const msg = 'There was an error loading the list of business types';
                    console.log(msg);
                    this.NotificationSvc.error(msg);
                }
            }
        );
    }

    public loadSocialMediaTypes() {
        this.fetchSocialMediaTypes().pipe(takeWhile(x => x !== null)).subscribe(res => {
            this.socialMediaTypes$.next(res);
        },
            (err: Error) => {
                const msg = 'There was an error loading the list of social media types';
                console.log(msg);
                this.NotificationSvc.error(msg);
            }
        );
    }

    public SetAuth0RegistrationToken(t: LgvModel.UserToken) {
        // Set user token and look up basic client info
        this.Auth0RegToken$.next(t);

        let token = this.Auth0RegToken$.value;
        let bldUrl = `${env.LgvApiBaseUrl}/subscribe/${token.type}/${token.guidStr}`;

        this.Auth0RegistrationState$.next('Have_Token');

        this.http.get<LgvModel.TokenLookupInfo>(bldUrl).pipe((first())).subscribe(x => {
            if (!!x && !!x.hasAcceptedTerms) {
                // user is using an old invitation link.  Take them to the Auth0 Login instead of registration page
                this.Auth0RegistrationState$.next('');
                this.lgvAuthMgr.Auth0Login();
            }
            else {
                this.PendingAuth0Registration$.next(x);
                this.Auth0RegistrationState$.next('AgentIsRegistering');
            }
        },
            err => this.Auth0RegistrationState$.next('Error')
        );
    }


    RegisterRealtor(realtorModel: LgvModel.RealtorRegistration) {

        let bldUrl = `${env.LgvApiBaseUrl}/public/realtor/register`;
        this.Auth0RegistrationState$.next('Registering');

        return this.http.post<any>(bldUrl, realtorModel).pipe(first(), tap((x) => {
            this.Auth0RegistrationState$.next('Completed');
        },
            err => this.Auth0RegistrationState$.next('Error')
        ));
    }

    Register(password, companyName) {

        let token = this.Auth0RegToken$.value;
        let bldUrl = `${env.LgvApiBaseUrl}/subscribe/${token.type}/${token.guidStr}`;

        let model = {
            id: token.guidStr,
            password,
            companyName
        };

        this.Auth0RegistrationState$.next('Registering');

        return this.http.post<any>(bldUrl, model).pipe(first(), tap((x) => {
            this.Auth0RegistrationState$.next('Completed');
        },
            err => this.Auth0RegistrationState$.next('Error')
        ));
    }

    SendInvitationEmail(userID: string): Observable<any> {

        let bldUrl = `${env.LgvApiBaseUrl}/emails/agent/invitation?agentId=${userID}`;
        let model = {};

        return this.http.post<LgvModel.Activity>(bldUrl, model).pipe(first());
    }

    public fetchCompanies(searchText: string = '', oData: string = '',) {
        if (!oData.includes('orderby')) {
            oData += '&$orderby=name';
        }
        let bldUrl = `${env.LgvApiBaseUrl}/companies?searchText=${searchText}${oData}`;
        return this.http.get<any>(bldUrl).pipe(first());
    }

    public SearchWebServiceOrganization(searchText: string = '', companyId: string = '', oDataSuffix?: string): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/organizations?companyId=${companyId}&txtSearch=${searchText}${!!oDataSuffix ? oDataSuffix : ''}`;
        return this.http.get<any>(bldUrl).pipe(first());
    }

    public fetchBusinessTypes(): Observable<LgvModel.BusinessType[]> {
        let bldUrl = `${env.LgvApiBaseUrl}/companies/business-types`;
        return this.http.get<LgvModel.BusinessType[]>(bldUrl).pipe(first());
    }

    public fetchSocialMediaTypes(): Observable<LgvModel.SocialMedia[]> {
        let bldUrl = `${env.LgvApiBaseUrl}/types/socialmedia`;
        return this.http.get<LgvModel.SocialMedia[]>(bldUrl).pipe(first());
    }

    public SaveOrganization(param) {
        let bldUrl = param.id ? `${env.LgvApiBaseUrl}/organizations/${param.id}` : `${env.LgvApiBaseUrl}/organizations`;
        return param.id ? this.http.patch<any>(bldUrl, param).pipe(first()) : this.http.post<any>(bldUrl, param).pipe(first());
    }
    public SaveCompany(param) {
        let bldUrl = param.id ? `${env.LgvApiBaseUrl}/companies/${param.id}` : `${env.LgvApiBaseUrl}/companies`;
        return param.id ? this.http.patch<any>(bldUrl, param).pipe(first()) : this.http.post<any>(bldUrl, param).pipe(first());
    }

    public FetchPublicRecordReportByAddress(searchModel: LgvPublicRecordModel.PublicRecordSearchByAddress): Observable<LgvPublicRecordModel.PublicRecordSearchResult> {
        let bldUrl = `${env.LgvApiBaseUrl}/publicrecords/by-address`;
        return this.http.post<any>(bldUrl, searchModel).pipe(first());
    }

    public FetchPublicRecordReportByID(propertyId: string, clientId: string): Observable<LgvPublicRecordModel.PublicRecordSearchResult> {
        let searchModel = {
            propertyId: propertyId,
            clientId: clientId
        }
        let bldUrl = `${env.LgvApiBaseUrl}/publicrecords/by-id`;
        return this.http.post<any>(bldUrl, searchModel).pipe(first());
    }

    public FetchPublicRecordHistory(propertyId: string, clientId: string) {
        let bldUrl = `${env.LgvApiBaseUrl}/publicrecords/requests?propertyId=${propertyId}&clientId=${clientId}`;
        return this.http.get<any>(bldUrl).pipe(first());
    }

    public navigateToHomePage() {
        this.defaultHomePage$.pipe(first(f => f !== null)).subscribe(path => {
            this.router.navigate([path]);
        })
    }

    public sendUserInvite(userID, requestBody: LgvModel.UserInviteModel) {
        const bldUrl = `${env.LgvApiBaseUrl}/users/${userID}/invites?sendInvite=true`;

        return this.http.post<any>(bldUrl, requestBody).pipe(first());
    }

    public checkForSampleClientsAndAddIfNeeded(isOnboarding: boolean) {
        // including silenced clients...
        let bldUrl = `${env.LgvApiBaseUrl}/clients?agentId=${this.lgvUserSvc.AgentID}&$count=true&$skip=0&$top=50`;

        this.http.get<any>(bldUrl).pipe(first()).subscribe(x => {

            let needSampleData = false;

            if (x.count === 0) {
                needSampleData = isOnboarding ? true : false;
                this.SampleClients$.next([]);
                this.SampleClientDataHasChanged$.next(true);
                this.HasRealClient$.next(false);
            }
            else {
                let clientResults: LgvModel.ClientInfo[] = x.value as LgvModel.ClientInfo[];
                let activeSampleClientResults = clientResults.filter(c => (!(!!c.isSilenced)) && !!c.email && c.email.includes('sample.data'));
                let haveRealClients = clientResults.findIndex(c => (!(!!c.isSilenced)) && (!(!!c.email) || !c.email.includes('sample.data'))) !== -1;
                this.SampleClients$.next(activeSampleClientResults);
                this.SampleClientDataHasChanged$.next(true);
                this.HasRealClient$.next(haveRealClients);
                needSampleData = isOnboarding && !haveRealClients && activeSampleClientResults.length === 0;
            }

            /* TESTING - Uncomment this to add sample data *AGAIN* (added prior, but sileneced) */
            //if(isOnboarding &&!needSampleData) {
            //    const clientResults = <LgvModel.ClientInfo[]>x.value;
            //    let haveUnSilencedClients = clientResults.findIndex(c => !c.isSilenced) > -1;
            //    if (!haveUnSilencedClients) {
            //        console.log('No Unsilenced Clients... Adding Sample Data')
            //        needSampleData = true;
            //    }
            //}

            if (needSampleData) {
                this.generateSampleBatchData();
            }

        },
            err => {
                console.log('Error fetching clients for onboarding agent.')
                console.log(err);
            });

    }

    generateSampleBatchData() {


        this.lgvUserSvc.AgentDTO$.pipe(first()).subscribe(agentDTO => {
            if (!(!!agentDTO)) {
                this.notificationSvc.warn('Please select an agent and try again.');
                return;
            }

            this.notificationSvc.success(`Adding sample data.`);
            let ownerId = agentDTO.agentInfo.id;
            let sampleClients = this.lgvMockDataSvc.mockClients(ownerId);

            let i = 0;
            for (let b of sampleClients) {
                this.generateSampleClientData(ownerId, b.clientBody, b.propertyBody, i++ == sampleClients.length - 1);
            }
        });
    }


    public generateSampleClientData(ownerId: string, clientBody: any, propertyBody: any, isLast: boolean) {
        clientBody.ownerId = ownerId;
        const clientId = '';

        this.lgvClientSvc.SaveClient(clientBody, clientId).pipe(
            first(),
            mergeMap((clientResponse) => {
                return zip(
                    this.lgvPropertySvc.addClientProperty(clientResponse.id, propertyBody),
                    of(clientResponse)
                );
            }),
            mergeMap(([propertyResponse, clientResponse]) => {
                return zip(
                    of(propertyResponse),
                    of(clientResponse),
                    this.FetchPublicRecordReportByID(propertyResponse.id, clientResponse.id)
                );
            })
        ).subscribe(([propertyResponse, clientResponse, publicRecordResponse]) => {
            if (propertyResponse) {

                // take the original request, add the ID generated, then add the client to sampleClients$
                let clientInfo = clientBody;
                clientInfo.id = clientResponse.id;
                let sampleClients = this.SampleClients$.value;
                sampleClients.push(clientInfo);
                this.SampleClients$.next(sampleClients);
                this.createLoanFromPublicRecords(publicRecordResponse);

                if (isLast) {
                    this.notificationSvc.success(`Sample clients have been added.`);

                    setTimeout(() => {
                        this.SampleClientDataHasChanged$.next(true);
                    }, 1000);
                }
            }
        }, (err) => {
            //this.isLoading$.next(false);
            this.notificationSvc.error(`There was an error adding ${clientBody.firstName} ${clientBody.lastName}`);
            console.log(err);
        })
    }

    createLoanFromPublicRecords(prResult: LgvPublicRecordModel.PublicRecordSearchResult) {
        this.lgvClientSvc.MakeDTResultTheActiveLoan(prResult.publicRecord.request.id, prResult.publicRecord.result.id).pipe(first()).subscribe(res => {

        }, (err) => {
            console.log('Error creating loan from property result');
            console.log(err);
        });
    }

    removeSampleData() {

        let bldUrl = `${env.LgvApiBaseUrl}/clients?agentId=${this.lgvUserSvc.AgentID}&IsSilenced=false&$count=true&$skip=0&$top=50`;
        this.SampleClients$.next([]);

        this.http.get<any>(bldUrl).pipe(first()).subscribe(x => {

            if (x.count > 0) {
                let clientResults: LgvModel.ClientInfo[] = x.value as LgvModel.ClientInfo[];
                clientResults = clientResults.filter(c => !!c && c.email?.includes('sample.data'));

                let i = 0;
                for (let c of clientResults) {
                    this.removeThisSampleClient(c.id, i++ === clientResults.length - 1);
                }
            }
        },
            err => {
                console.log('Error fetching clients for onboarding agent.')
                console.log(err);
            });

    }

    removeThisSampleClient(clientId: string, isLast: boolean = false) {
        this.lgvClientSvc.SilenceThisClient(clientId, true).pipe(first()).subscribe(x => {
            if (isLast) {
                this.notificationSvc.success(`Sample clients have been removed.`);
                this.SampleClientDataHasChanged$.next(true);
                //this.HaveSampleClients$.next(false);
            }
        },
            err => {
                this.NotificationSvc.error(`There was an error trying to remove a sample client.`);
            }
        );
    }

}
